import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import ResultStatus from "../classes/ResultStatus";
import ScrollView2 from "./ScrollView2";
import ChangePasswordRequest from "../models/ChangePasswordRequest";
import Developer from "../models/Developer";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import Result from "../classes/Result";
import CollectionUtils from "../utils/CollectionUtils";
import IconView from "./IconView";
import LabelWithStringField from "./LabelWithStringField";
import { BuildContext } from "../classes/BuildContext";

type _BuilderChangePasswordScreenOnLoginUser = (user: Developer) => void;

type _DoneRBOnPressed = (d3eState: BuilderChangePasswordScreenRefs) => void;

type _TextFieldNewOnChanged = (
  text: string,
  d3eState: BuilderChangePasswordScreenRefs
) => void;

type _TextFieldOnChanged = (
  text: string,
  d3eState: BuilderChangePasswordScreenRefs
) => void;

export interface BuilderChangePasswordScreenProps extends BaseUIProps {
  key?: string;
  isNewUser: boolean;
  user: Developer;
  onLoginUser: _BuilderChangePasswordScreenOnLoginUser;
}
/// To store state data for BuilderChangePasswordScreen
class BuilderChangePasswordScreenRefs {
  columnScrollController: ui.ScrollController = new ui.ScrollController();
  public doneRB: DoneRBState = new DoneRBState();
}

interface DoneRBWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderChangePasswordScreenRefs;
  _onPasswordSet?: _DoneRBOnPressed;
}

class DoneRBState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _DoneRBWithState extends ObservableComponent<DoneRBWithStateProps> {
  doneRBFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DoneRBWithStateProps) {
    super(props);

    this.initState();
  }
  public get doneRB(): DoneRBState {
    return this.props.d3eState.doneRB;
  }
  public get d3eState(): BuilderChangePasswordScreenRefs {
    return this.props.d3eState;
  }
  public get _onPasswordSet(): _DoneRBOnPressed {
    return this.props._onPasswordSet;
  }
  public initState() {
    super.initState();

    this.updateObservable("doneRB", null, this.doneRB);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["doneRB", "doneRB.", "doneRB.focus", "doneRB.hover"],
      this.rebuild
    );
  }
  public doneRBOnEnter(event): void {
    return this.doneRB.setHover(true);
  }
  public doneRBOnExit(event): void {
    return this.doneRB.setHover(false);
  }
  public doneRBOnFocusChange(val): void {
    return this.doneRB.setFocus(val);
  }
  public dispose(): void {
    this.doneRB.setHover(false);

    this.doneRB.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.doneRB.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.doneRB.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.doneRB.disable,
        onPressed: () => {
          this._onPasswordSet(this.d3eState);
        },
        onFocusChange: (val) => {
          this.doneRBOnFocusChange(val);
        },
        child: TextView({ data: "Submit", className: "hc" }),
        onEnter: (event) => {
          this.doneRBOnEnter(event);
        },
        onExit: (event) => {
          this.doneRBOnExit(event);
        },
      }),
      className: "xbf9 hc",
    });
  }
}
function DoneRBWithState(props: DoneRBWithStateProps) {
  return React.createElement(_DoneRBWithState, props);
}

class _BuilderChangePasswordScreenState extends ObservableComponent<BuilderChangePasswordScreenProps> {
  static defaultProps = { isNewUser: false, user: null, onLoginUser: null };
  d3eState: BuilderChangePasswordScreenRefs =
    new BuilderChangePasswordScreenRefs();
  newPassword: string = "";
  confirmPassword: string = "";
  isUpperCaseMissing: boolean = false;
  isLowerCaseMissing: boolean = false;
  isContainsSpace: boolean = false;
  isSpecialCharacterMissing: boolean = false;
  isNumberMissing: boolean = false;
  isNotMatchedPassword: boolean = false;
  isNotMinimumLengthPwd: boolean = false;
  errorMessages: Array<string> = ListWrapper.widget(this, "errorMessages");
  showErrors: boolean = false;
  showSuccess: boolean = false;
  showValidationError: boolean = false;
  showPasswordError: boolean = false;
  showConfirmPasswordError: boolean = false;
  showNewPasswordError: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuilderChangePasswordScreenProps) {
    super(props);

    this.initState();
  }
  public get isNewUser(): boolean {
    return this.props.isNewUser;
  }
  public get user(): Developer {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("user", this.props.user);

    this.on(["newPassword"], this.computeIsUpperCaseMissing);

    this.computeIsUpperCaseMissing();

    this.on(["newPassword"], this.computeIsLowerCaseMissing);

    this.computeIsLowerCaseMissing();

    this.on(["newPassword"], this.computeIsContainsSpace);

    this.computeIsContainsSpace();

    this.on(["newPassword"], this.computeIsSpecialCharacterMissing);

    this.computeIsSpecialCharacterMissing();

    this.on(["newPassword"], this.computeIsNumberMissing);

    this.computeIsNumberMissing();

    this.on(["newPassword"], this.computeIsNotMinimumLengthPwd);

    this.computeIsNotMinimumLengthPwd();

    this.on(
      [
        "confirmPassword",
        "isContainsSpace",
        "isLowerCaseMissing",
        "isNewUser",
        "isNotMatchedPassword",
        "isNotMinimumLengthPwd",
        "isNumberMissing",
        "isSpecialCharacterMissing",
        "isUpperCaseMissing",
        "newPassword",
        "showConfirmPasswordError",
        "showNewPasswordError",
        "showPasswordError",
        "showSuccess",
        "showValidationError",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: BuilderChangePasswordScreenProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.isNewUser !== this.props.isNewUser) {
      this.fire("isNewUser", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public setNewPassword(val: string): void {
    let isValChanged: boolean = this.newPassword !== val;

    if (!isValChanged) {
      return;
    }

    this.newPassword = val;

    this.fire("newPassword", this);
  }
  public setConfirmPassword(val: string): void {
    let isValChanged: boolean = this.confirmPassword !== val;

    if (!isValChanged) {
      return;
    }

    this.confirmPassword = val;

    this.fire("confirmPassword", this);
  }
  public setIsUpperCaseMissing(val: boolean): void {
    let isValChanged: boolean = this.isUpperCaseMissing !== val;

    if (!isValChanged) {
      return;
    }

    this.isUpperCaseMissing = val;

    this.fire("isUpperCaseMissing", this);
  }
  public computeIsUpperCaseMissing = (): void => {
    try {
      this.setIsUpperCaseMissing(
        !new RegExp(".*[A-Z].*").hasMatch(this.newPassword)
      );
    } catch (exception) {
      console.log(
        " exception in computeIsUpperCaseMissing : " + exception.toString()
      );

      this.setIsUpperCaseMissing(false);
    }
  };
  public setIsLowerCaseMissing(val: boolean): void {
    let isValChanged: boolean = this.isLowerCaseMissing !== val;

    if (!isValChanged) {
      return;
    }

    this.isLowerCaseMissing = val;

    this.fire("isLowerCaseMissing", this);
  }
  public computeIsLowerCaseMissing = (): void => {
    try {
      this.setIsLowerCaseMissing(
        !new RegExp(".*[a-z].*").hasMatch(this.newPassword)
      );
    } catch (exception) {
      console.log(
        " exception in computeIsLowerCaseMissing : " + exception.toString()
      );

      this.setIsLowerCaseMissing(false);
    }
  };
  public setIsContainsSpace(val: boolean): void {
    let isValChanged: boolean = this.isContainsSpace !== val;

    if (!isValChanged) {
      return;
    }

    this.isContainsSpace = val;

    this.fire("isContainsSpace", this);
  }
  public computeIsContainsSpace = (): void => {
    try {
      this.setIsContainsSpace(this.newPassword.contains(" "));
    } catch (exception) {
      console.log(
        " exception in computeIsContainsSpace : " + exception.toString()
      );

      this.setIsContainsSpace(false);
    }
  };
  public setIsSpecialCharacterMissing(val: boolean): void {
    let isValChanged: boolean = this.isSpecialCharacterMissing !== val;

    if (!isValChanged) {
      return;
    }

    this.isSpecialCharacterMissing = val;

    this.fire("isSpecialCharacterMissing", this);
  }
  public computeIsSpecialCharacterMissing = (): void => {
    try {
      this.setIsSpecialCharacterMissing(
        !new RegExp(".*[!@#$%&*].*").hasMatch(this.newPassword)
      );
    } catch (exception) {
      console.log(
        " exception in computeIsSpecialCharacterMissing : " +
          exception.toString()
      );

      this.setIsSpecialCharacterMissing(false);
    }
  };
  public setIsNumberMissing(val: boolean): void {
    let isValChanged: boolean = this.isNumberMissing !== val;

    if (!isValChanged) {
      return;
    }

    this.isNumberMissing = val;

    this.fire("isNumberMissing", this);
  }
  public computeIsNumberMissing = (): void => {
    try {
      this.setIsNumberMissing(
        !new RegExp(".*[0-9].*").hasMatch(this.newPassword)
      );
    } catch (exception) {
      console.log(
        " exception in computeIsNumberMissing : " + exception.toString()
      );

      this.setIsNumberMissing(false);
    }
  };
  public setIsNotMatchedPassword(val: boolean): void {
    let isValChanged: boolean = this.isNotMatchedPassword !== val;

    if (!isValChanged) {
      return;
    }

    this.isNotMatchedPassword = val;

    this.fire("isNotMatchedPassword", this);
  }
  public setIsNotMinimumLengthPwd(val: boolean): void {
    let isValChanged: boolean = this.isNotMinimumLengthPwd !== val;

    if (!isValChanged) {
      return;
    }

    this.isNotMinimumLengthPwd = val;

    this.fire("isNotMinimumLengthPwd", this);
  }
  public computeIsNotMinimumLengthPwd = (): void => {
    try {
      this.setIsNotMinimumLengthPwd(!(this.newPassword.length >= 8));
    } catch (exception) {
      console.log(
        " exception in computeIsNotMinimumLengthPwd : " + exception.toString()
      );

      this.setIsNotMinimumLengthPwd(false);
    }
  };
  public setErrorMessages(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.errorMessages,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.errorMessages.clear();

    this.errorMessages.addAll(val);

    this.fire("errorMessages", this);
  }
  public addToErrorMessages(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errorMessages.contains(val)) this.errorMessages.add(val);
    } else {
      this.errorMessages.remove(this.errorMessages.elementAt(index));

      this.errorMessages.add(val);
    }

    this.fire("errorMessages", this, val, true);
  }
  public removeFromErrorMessages(val: string): void {
    this.errorMessages.remove(val);

    this.fire("errorMessages", this, val, false);
  }
  public setShowErrors(val: boolean): void {
    let isValChanged: boolean = this.showErrors !== val;

    if (!isValChanged) {
      return;
    }

    this.showErrors = val;

    this.fire("showErrors", this);
  }
  public setShowSuccess(val: boolean): void {
    let isValChanged: boolean = this.showSuccess !== val;

    if (!isValChanged) {
      return;
    }

    this.showSuccess = val;

    this.fire("showSuccess", this);
  }
  public setShowValidationError(val: boolean): void {
    let isValChanged: boolean = this.showValidationError !== val;

    if (!isValChanged) {
      return;
    }

    this.showValidationError = val;

    this.fire("showValidationError", this);
  }
  public setShowPasswordError(val: boolean): void {
    let isValChanged: boolean = this.showPasswordError !== val;

    if (!isValChanged) {
      return;
    }

    this.showPasswordError = val;

    this.fire("showPasswordError", this);
  }
  public setShowConfirmPasswordError(val: boolean): void {
    let isValChanged: boolean = this.showConfirmPasswordError !== val;

    if (!isValChanged) {
      return;
    }

    this.showConfirmPasswordError = val;

    this.fire("showConfirmPasswordError", this);
  }
  public setShowNewPasswordError(val: boolean): void {
    let isValChanged: boolean = this.showNewPasswordError !== val;

    if (!isValChanged) {
      return;
    }

    this.showNewPasswordError = val;

    this.fire("showNewPasswordError", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ScrollView2({
      child: ui.Column({
        mainAxisAlignment: ui.MainAxisAlignment.center,
        children: [
          TextView({
            data: this.isNewUser ? "Set Password" : "Change Password",
            textAlign: cStyle.tTextViewHeadingTextAlignOn,
            style: new ui.TextStyle({
              color: cStyle.tTextViewHeadingColorOn,
              fontSize: cStyle.tTextViewHeadingFontSizeOn,
              fontWeight: cStyle.tTextViewHeadingFontWeightOn,
            }),
            className: "xe8 hc",
            key: "0",
          }),
          ui.Column({
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              ui.Container({
                width: 387,
                child: LabelWithStringField({
                  name: "New Password",
                  value: this.newPassword,
                  placeHolder: "Enter New Password",
                  obscureText: true,
                  autoFocus: true,
                  onTap: () => {
                    this.passwordError(this.d3eState);
                  },
                  onChanged: (text) => {
                    this.textFieldNewonChanged(text, this.d3eState);

                    this.newPasswordSet(text, this.d3eState);
                  },
                  errors: this.showNewPasswordError
                    ? ["Please enter new password"]
                    : [],
                }),
                key: "0",
                className: "x5e hc",
              }),
            ],
            className: "x04 hc",
            key: "1",
          }),
          ui.Container({ className: "xde hc vc", key: "2" }),
          ui.Column({
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              TextView({
                data: "Password Rules:",
                textAlign: cStyle.tTextViewLabelTextTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewLabelTextColorOn,
                  fontSize: cStyle.tTextViewLabelTextFontSizeOn,
                  fontWeight: cStyle.tTextViewLabelTextFontWeightOn,
                }),
                className: "x181 hc",
                key: "0",
              }),
              ui.Wrap({
                spacing: 5.0,
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  !this.isNotMinimumLengthPwd
                    ? IconView({
                        icon: MaterialIcons.done,
                        size: 15,
                        color: cStyle.c13,
                        className: "x0e hc",
                      })
                    : [],
                  TextView({
                    data: "Password must be atleast 8 characters long.",
                    overflow: cStyle.tTextViewListTextOverflowOn,
                    textAlign: cStyle.tTextViewListTextTextAlignOn,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewListTextColorOn,
                      fontSize: cStyle.tTextViewListTextFontSizeOn,
                    }),
                    className: "xb5 hc",
                    key: "1",
                  }),
                ],
                className: "x7e hc",
                key: "1",
              }),
              ui.Wrap({
                spacing: 5.0,
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  !this.isUpperCaseMissing && !this.isLowerCaseMissing
                    ? IconView({
                        icon: MaterialIcons.done,
                        size: 15,
                        color: cStyle.c13,
                        className: "xb7 hc",
                      })
                    : [],
                  TextView({
                    data: "Password must include 1 Uppercase and 1 Lowercase letter.",
                    overflow: cStyle.tTextViewListTextOverflowOn,
                    textAlign: cStyle.tTextViewListTextTextAlignOn,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewListTextColorOn,
                      fontSize: cStyle.tTextViewListTextFontSizeOn,
                    }),
                    className: "xcb hc",
                    key: "1",
                  }),
                ],
                className: "xc8 hc",
                key: "2",
              }),
              ui.Wrap({
                spacing: 5.0,
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  !this.isNumberMissing
                    ? IconView({
                        icon: MaterialIcons.done,
                        size: 15,
                        color: cStyle.c13,
                        className: "xdf hc",
                      })
                    : [],
                  TextView({
                    data: "Password must contain 1 numeric character.",
                    overflow: cStyle.tTextViewListTextOverflowOn,
                    textAlign: cStyle.tTextViewListTextTextAlignOn,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewListTextColorOn,
                      fontSize: cStyle.tTextViewListTextFontSizeOn,
                    }),
                    className: "xf6 hc",
                    key: "1",
                  }),
                ],
                className: "x0a hc",
                key: "3",
              }),
              ui.Wrap({
                spacing: 5.0,
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  !this.isSpecialCharacterMissing
                    ? IconView({
                        icon: MaterialIcons.done,
                        size: 15,
                        color: cStyle.c13,
                        className: "x71 hc",
                      })
                    : [],
                  TextView({
                    data: "Password must contain 1 special character.",
                    overflow: cStyle.tTextViewListTextOverflowOn,
                    textAlign: cStyle.tTextViewListTextTextAlignOn,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewListTextColorOn,
                      fontSize: cStyle.tTextViewListTextFontSizeOn,
                    }),
                    className: "xff hc",
                    key: "1",
                  }),
                ],
                className: "x18f hc",
                key: "4",
              }),
              ui.Wrap({
                spacing: 5.0,
                crossAxisAlignment: ui.WrapCrossAlignment.center,
                children: [
                  !this.isContainsSpace && this.newPassword !== ""
                    ? IconView({
                        icon: MaterialIcons.done,
                        size: 15,
                        color: cStyle.c13,
                        className: "x3a hc",
                      })
                    : [],
                  TextView({
                    data: "Password should not contain any space.",
                    overflow: cStyle.tTextViewListTextOverflowOn,
                    textAlign: cStyle.tTextViewListTextTextAlignOn,
                    style: new ui.TextStyle({
                      color: cStyle.tTextViewListTextColorOn,
                      fontSize: cStyle.tTextViewListTextFontSizeOn,
                    }),
                    className: "x685 hc",
                    key: "1",
                  }),
                ],
                className: "x1d hc",
                key: "5",
              }),
            ],
            className: "x2a hc",
            key: "3",
          }),
          ui.Container({ className: "x45d hc vc", key: "4" }),
          ui.Column({
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              ui.Container({
                width: 387,
                child: LabelWithStringField({
                  name: "Confirm Password",
                  value: this.confirmPassword,
                  help: "",
                  helpUrl: "",
                  placeHolder: "Enter Confirm Password",
                  obscureText: true,
                  onTap: () => {
                    this.confirmPasswordError(this.d3eState);
                  },
                  onSubmitted: (value) => {
                    this.onLoginWithEnterKey(value, this.d3eState);
                  },
                  onChanged: (text) => {
                    this.textFieldonChanged(text, this.d3eState);

                    this.confirmPasswordSet(text, this.d3eState);
                  },
                  errors: this.showPasswordError
                    ? ["Please enter new password and confirm password"]
                    : this.isNotMatchedPassword
                    ? ["Password and Confirm Password must match"]
                    : this.showValidationError
                    ? ["Password does not meet password rules"]
                    : this.showConfirmPasswordError
                    ? ["Please enter  confirm password"]
                    : [],
                }),
                key: "0",
                className: "xf4 hc",
              }),
            ],
            className: "x1e hc",
            key: "5",
          }),
          ui.Container({ className: "x32c hc vc", key: "6" }),
          ui.Column({
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              DoneRBWithState({
                d3eState: this.d3eState,
                _onPasswordSet: this.onPasswordSet,
                key: "0",
              }),
              this.showSuccess
                ? ui.Row({
                    children: [
                      TextView({
                        data: "Password updated successFully !",
                        textAlign: cStyle.tTextViewSuccessErrorTextTextAlignOn,
                        style: new ui.TextStyle({
                          color: cStyle.tTextViewSuccessErrorTextColorOn,
                          fontSize: cStyle.tTextViewSuccessErrorTextFontSizeOn,
                        }),
                        className: "x1f",
                        key: "0",
                      }),
                    ],
                    className: "x2d hc",
                  })
                : [],
            ],
            className: "x66 hc",
            key: "7",
          }),
        ],
        className: ui.join(
          this.props.className,
          "BuilderChangePasswordScreen x6a hc"
        ),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "BuilderChangePasswordScreen x6a hc",
      controller: this.d3eState.columnScrollController,
    });
  }
  public onPasswordSet = (d3eState: BuilderChangePasswordScreenRefs): void => {
    this.onChangeValidation();
  };
  public onLoginWithEnterKey = (
    value: string,
    d3eState: BuilderChangePasswordScreenRefs
  ): void => {
    this.onChangeValidation();
  };
  public newPasswordSet = (
    value: string,
    d3eState: BuilderChangePasswordScreenRefs
  ): void => {
    this.setIsNotMinimumLengthPwd(false);

    this.setNewPassword(value);
  };
  public confirmPasswordSet = (
    value: string,
    d3eState: BuilderChangePasswordScreenRefs
  ): void => {
    this.setIsNotMatchedPassword(false);

    this.setConfirmPassword(value);
  };
  public onChangePassword = async (): Promise<void> => {
    this.setShowErrors(false);

    this.setShowPasswordError(false);

    this.setShowConfirmPasswordError(false);

    this.setShowSuccess(false);

    let result: Result<ChangePasswordRequest> = await new ChangePasswordRequest(
      { newPassword: this.newPassword }
    ).save();

    if (result.status === ResultStatus.Success) {
      this.setShowSuccess(true);

      this.onLoginUser(this.user);
    } else {
      this.setErrorMessages(result.errors);

      this.setShowErrors(true);

      // this.showValidationError = true;
    }
  };
  public onChangeValidation = (): void => {
    //  isNotMinimumLengthPwd = !(this.newPassword.length > 8);

    //  isNotMatchedPassword = !(this.newPassword == this.confirmPassword);

    //  this.isNumberMissing=!RegExp('^(?=.*[0-9])').hasMatch(newPassword);

    //  this.isSpecialCharacterMissing=!RegExp('^(?=.*[!@#$%&*])').hasMatch(newPassword);

    //  this.isUpperCaseMissing=!RegExp('^(?=.*[A-Z])').hasMatch(newPassword);

    //  this.isLowerCaseMissing=!RegExp('^(?=.*[a-z])').hasMatch(newPassword);

    if (this.newPassword.contains(" ")) {
      this.setIsContainsSpace(true);
    } else if (
      this.newPassword.isNotEmpty &&
      this.confirmPassword.isNotEmpty &&
      this.newPassword !== this.confirmPassword
    ) {
      this.setIsNotMatchedPassword(true);
    } else if (
      this.newPassword.isNotEmpty &&
      this.confirmPassword.length <= 0
    ) {
      this.setShowConfirmPasswordError(true);
    } else if (
      this.confirmPassword.isNotEmpty &&
      this.newPassword.length <= 0
    ) {
      this.setShowNewPasswordError(true);
    } else if (
      this.newPassword.length <= 0 &&
      this.confirmPassword.length <= 0
    ) {
      this.setShowPasswordError(true);
    } else if (
      !(
        this.isNumberMissing ||
        this.isSpecialCharacterMissing ||
        this.isUpperCaseMissing ||
        this.isLowerCaseMissing ||
        this.isContainsSpace ||
        this.isNotMinimumLengthPwd
      )
    ) {
      this.onChangePassword();
    } else {
      this.setShowValidationError(true);

      return;
    }
  };
  public confirmPasswordError = (
    d3eState: BuilderChangePasswordScreenRefs
  ): void => {
    this.setShowSuccess(false);

    this.setShowValidationError(false);

    this.setIsNotMatchedPassword(false);

    this.setShowPasswordError(false);

    this.setShowConfirmPasswordError(false);

    this.setShowNewPasswordError(false);
  };
  public passwordError = (d3eState: BuilderChangePasswordScreenRefs): void => {
    this.setShowSuccess(false);

    this.setShowValidationError(false);

    this.setShowPasswordError(false);

    this.setShowConfirmPasswordError(false);

    this.setShowNewPasswordError(false);
  };
  public get onLoginUser(): _BuilderChangePasswordScreenOnLoginUser {
    return this.props.onLoginUser;
  }
  public textFieldNewonChanged = (
    val: string,
    d3eState: BuilderChangePasswordScreenRefs
  ): void => {
    this.setNewPassword(val);
  };
  public textFieldonChanged = (
    val: string,
    d3eState: BuilderChangePasswordScreenRefs
  ): void => {
    this.setConfirmPassword(val);
  };
  public get doneRB() {
    return this.d3eState.doneRB;
  }
}
export default function BuilderChangePasswordScreen(
  props: BuilderChangePasswordScreenProps
) {
  return React.createElement(_BuilderChangePasswordScreenState, {
    ..._BuilderChangePasswordScreenState.defaultProps,
    ...props,
  });
}
